import React, { useEffect, useState, useCallback } from "react";
import { Bar } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { app_url } from "../../config";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import debounce from "lodash.debounce";
import Loader from "../Loader";
import Error500 from "../500/Error500";

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Leadsales = () => {
  const token = JSON.parse(localStorage.getItem("adminEtijwaal"))?.token;

  const getLastFiveYears = () => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 5 }, (_, i) => currentYear - i);
  };

  const [selectedYear, setSelectedYear] = useState(null);
  const [years, setYears] = useState(getLastFiveYears());
  const [isLoading, setIsLoading] = useState(true);
  const [leadSales, setLeadSales] = useState([]);
  const [months, setMonths] = useState([]);
  const [width, setWidth] = useState(window.innerWidth);
  const navigate = useNavigate();
  const [networkIssue, setNetworkIssue] = useState(false);
  const [error, setError] = useState("");

  const fetchData = useCallback(
    async (year) => {
      setIsLoading(true);
      try {
        const res = await axios.get(
          `${app_url}/api/dashboard/getAdminDashboardLeadSales?year=${year}`,
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setLeadSales(res?.data?.leadSales?.monthlySales);
        setMonths(res?.data?.leadSales?.months);
        setIsLoading(false);
      } catch (err) {
        setIsLoading(false);
        if (err.response) {
          if (err.response.status === 400) {
            toast.error(err.response.data.message);
          } else if (err.response.status === 401) {
            navigate("/");
          } else if (err.response.status === 500) {
            toast.error(err.response.data.message);
          } else {
            toast.error("An unexpected error occurred.");
          }
        } else {
          setNetworkIssue(true);
          setError("Network error. Please check your internet connection.");
        }
      }
    },
    [token, navigate]
  );

  useEffect(() => {
    setSelectedYear(years[0]);
  }, [years]);

  useEffect(() => {
    if (selectedYear !== null) {
      fetchData(selectedYear);
    }
  }, [selectedYear, fetchData]);

  useEffect(() => {
    const handleResize = debounce(() => setWidth(window.innerWidth), 500);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const media = width >= 0 && width <= 758;

  const data = {
    labels:
      months.length > 0
        ? months
        : [
            "January",
            "February",
            "March",
            "April",
            "May",
            "June",
            "July",
            "August",
            "September",
            "October",
            "November",
            "December",
          ],
    datasets: [
      {
        label: `Total Sales USD (${selectedYear || ""})`,
        data: leadSales.length > 0 ? leadSales : Array(12).fill(0),
        backgroundColor: [
          "#a7a7a7f1",
          "#f79a4fff",
          "#a7a7a7f1",
          "#f79a4fff",
          "#a7a7a7f1",
          "#f79a4fff",
          "#a7a7a7f1",
          "#f79a4fff",
          "#a7a7a7f1",
          "#f79a4fff",
          "#a7a7a7f1",
          "#f79a4fff",
        ],
        borderColor: "#fff",
        borderWidth: 1,
        borderRadius: 5,
        color: "#fff",
        hoverBackgroundColor: [
          "gray",
          "#ED8936",
          "gray",
          "#ED8936",
          "gray",
          "#ED8936",
          "gray",
          "#ED8936",
          "gray",
          "#ED8936",
          "gray",
          "#ED8936",
        ],
      },
    ],
  };

  // Chart options
  const options = {
    responsive: true,
    maintainAspectRatio: true,
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: {
            size: media ? 10 : 12,
          },
        },
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            return `$${context.parsed.y.toFixed(2)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: media ? 9 : 11,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0, 0, 0, 0.05)",
        },
        ticks: {
          stepSize: 1000,
          callback: function (value) {
            return `$${value}`;
          },
          font: {
            size: media ? 9 : 11,
          },
        },
      },
    },
  };

  if (networkIssue) {
    return <Error500 message={error} />;
  }

  return (
    <div className="card p-3">
      <div className="my-4">
        {years.map((year) => (
          <button
            key={year}
            onClick={() => setSelectedYear(year)}
            className={
              selectedYear === year ? "btn ms-0 dark-btn" : "btn ms-0 chart-btn"
            }
            bg="transparent"
          >
            {year}
          </button>
        ))}
      </div>

      {isLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "400px" }}
        >
          <Loader />
        </div>
      ) : (
        <Bar data={data} options={options} />
      )}

      {leadSales.length === 0 && !isLoading && (
        <div className="text-center py-5">
          <p className="text-muted">
            No sales data available for {selectedYear}
          </p>
        </div>
      )}
    </div>
  );
};

export default Leadsales;
